import React, { useState } from "react";
import ContentWrapper from "../../../common/components/ContentWrapper";
import PageBanner from "../../../common/components/PageBanner";
import GovermentModalForm from "../Home/components/GovermentModalForm";
import {
  GOV_VIDEO_URL,
  afterAlertGroups,
  audiences,
  beforeItems,
  familyQuestions,
  flowSteps,
  governmentPrepares,
  residentsMust,
  residentCapabilities,
  imagineLines,
  leaderBenefits,
} from "./govContent";
import { CONTACT_EMAIL, CONTACT_PHONE, CONTACT_PHONE_LABEL } from "./govContact";
import "./government.css";

import GOVERNMENT_BANNER from "../../../assets/new-images/Government.png";
import SCREEN_FORMAT from "../../../assets/new-images/screen_format.png";
import FAMILY_PET_PHOTO from "../../../assets/family-pet.png";
import APP_SCREENSHOT from "../../../assets/new-images/MYREDFOLDER-App-Card.png";
import MY_FAMILY_SCREENSHOT from "../../../assets/new-images/emergency_during.png";
import FLOOD_PHOTO from "../../../assets/new-images/brief-history_community.png";


const Government = () => {
  const [formOpen, setFormOpen] = useState(false);
  const openForm = () => setFormOpen(true);
  const closeForm = () => setFormOpen(false);

  return (
    <div className="gov-page">
      <PageBanner
        image={GOVERNMENT_BANNER}
        alt="Government and community leaders working together on emergency preparedness."
        eyebrow="For communities"
        title="Government"
      />

      {/* 2. Intro: the family questions, then what a program provides */}
      <section className="mrf-section gov-intro" aria-labelledby="gov-intro-heading">
        <div className="mrf-container">
          <h2 id="gov-intro-heading" className="gov-intro__title">
            One Community. Every Family. <span>One Preparedness Plan.</span>
          </h2>
          <p className="gov-intro__sub">Help turn community preparedness into household preparedness.</p>
          <div className="gov-talk">
            <div className="gov-talk__lead">
              <p className="gov-talk__from">Your emergency management team prepares for the community.</p>
              <p className="gov-talk__to">
                But when an emergency happens, <span>preparedness ultimately becomes personal.</span>
              </p>
            </div>
            <div className="gov-talk__chat" aria-label="Questions every family asks">
              <span className="gov-talk__label">Every family asks</span>
              <ul>
                {familyQuestions.map((q, i) => (
                  <li key={q} className={i % 2 ? "is-right" : ""}>
                    {q}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <p className="gov-intro__bridge">
            MYREDFOLDER® helps governments extend preparedness beyond Emergency Operations Centers, emergency plans,
            public alerts, and response agencies—and into the homes and hands of the families they serve.
          </p>

          <h3 className="gov-intro__sub">Through a government-sponsored MYREDFOLDER® program, residents can:</h3>
          <div className="gov-provides">
            {residentCapabilities.map((c, i) => (
              <div key={c.title} className="gov-provide">
                <span className="gov-provide__num">{i + 1}</span>
                <h4>{c.title}</h4>
                <p>{c.body}</p>
              </div>
            ))}
          </div>

          <div className="gov-officials">
            <span className="gov-officials__tag">For officials</span>
            <p>
              At the same time, authorized government officials can have a direct channel for delivering concise
              emergency and public-safety information to participating residents before, during, and after an incident.
            </p>
          </div>

          <p className="gov-tagline gov-tagline--center">
            Prepared government. Prepared residents. More resilient communities.
          </p>
        </div>
      </section>

      {/* 3. Video centerpiece */}
      {GOV_VIDEO_URL && (
        <section className="gov-video" aria-labelledby="gov-video-heading">
          <div className="gov-video__head">
            <div className="mrf-container">
              <span className="gov-video__eyebrow gov-video__eyebrow--light">Watch the overview</span>
              <h2 id="gov-video-heading" className="mrf-heading">
                See How MYREDFOLDER® Can Help Prepare an Entire Community
              </h2>
            </div>
          </div>
          <div className="mrf-container gov-video__body">
            <div className="gov-video__frame">
              <div className="mrf-video">
                <iframe
                  src={GOV_VIDEO_URL}
                  title="How MYREDFOLDER® can help prepare an entire community (video)"
                  loading="lazy"
                  allow="fullscreen; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
            <div className="gov-video__after">
              <div className="gov-video__copy">
                <span className="gov-video__eyebrow">The opportunity</span>
                <p className="gov-big">
                  What if your community could give every participating household the tools to build a family
                  emergency plan before the next emergency?
                </p>
                <p className="gov-video__support">
                  MYREDFOLDER® provides governments with a scalable, family-centered preparedness platform designed to
                  complement—not replace—existing emergency management, public warning, and public-safety systems.
                </p>
              </div>
              <div className="gov-video__cta">
                <p className="gov-video__cta-title">Start the conversation</p>
                <p className="gov-video__cta-text">
                  Tell us about your community and we'll help you explore what a MYREDFOLDER® deployment could look
                  like.
                </p>
                <button type="button" className="mrf-btn mrf-btn--primary" onClick={openForm}>
                  Explore a Government Partnership
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 4. Community to family */}
      <section className="mrf-section gov-bridge" aria-labelledby="gov-family-heading">
        <div className="mrf-container">
          <h2 id="gov-family-heading" className="mrf-heading mrf-heading--center">
            From Community Preparedness to Family Action
          </h2>
          <p className="mrf-lead mrf-lead--center">
            Government agencies invest significant time and resources in preparedness. But there is another critical
            component:
          </p>

          <div className="gov-bridge__grid">
            <div className="gov-bridge__col">
              <h3 className="gov-bridge__label">Government prepares</h3>
              <ul>
                {governmentPrepares.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="gov-bridge__core">
              <span className="gov-bridge__core-eyebrow">The missing piece</span>
              <p className="gov-bridge__core-title">The household.</p>
            </div>

            <div className="gov-bridge__col gov-bridge__col--residents">
              <h3 className="gov-bridge__label">Residents must</h3>
              <ul>
                {residentsMust.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </div>

          <div className="gov-bridge__close">
            <p>
              MYREDFOLDER® helps bridge the space between government preparedness and household preparedness.
            </p>
            <p className="gov-bridge__close-strong">
              Instead of simply encouraging residents to “have a plan,” a participating government can provide
              residents with a practical platform to help them build the plan.
            </p>
          </div>
        </div>
      </section>

      {/* 5. Before / During / After */}
      <section className="gov-bda" aria-labelledby="gov-bda-heading">
        <div className="mrf-container" style={{ paddingTop: 40 }}>
          <h2 id="gov-bda-heading" className="mrf-heading mrf-heading--center">
            Before. During. After.
          </h2>
        </div>

        <div className="gov-phase">
          <div className="mrf-container gov-phase__grid">
            <div>
              <span className="gov-phase__tag">BEFORE</span>
              <h3 className="mrf-heading">Help Residents Prepare Before Circumstances Become Urgent</h3>
              <p className="mrf-callout">The worst time to create an emergency plan is in the middle of an emergency.</p>
              <p className="mrf-body">
                MYREDFOLDER® encourages residents to prepare during normal conditions, when families have the time to
                think through the information, decisions, and resources they may need later. Participating households
                can use MYREDFOLDER® to organize family preparedness information such as:
              </p>
              <ul className="gov-checklist">
                {beforeItems.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <p className="mrf-body">
                <strong>The result is not simply another preparedness brochure or checklist. It is a plan built around
                the resident's own family.</strong>
              </p>
            </div>
            <div className="gov-phase__media">
              <img src={MY_FAMILY_SCREENSHOT} alt="MYREDFOLDER® family profile screen." loading="lazy" />
            </div>
          </div>
        </div>

        <div className="gov-phase">
          <div className="mrf-container gov-phase__grid">
            <div>
              <span className="gov-phase__tag">DURING</span>
              <h3 className="mrf-heading">Connect Information, Communication and Family</h3>
              <p className="mrf-body">
                An emergency does not necessarily happen while everyone is together. A parent may be at work. Children
                may be at school. Another family member may be shopping, traveling, or somewhere else in the community.
                That is when preparedness must become action.
              </p>
              <p className="mrf-body">
                MYREDFOLDER® provides participating families with emergency-related tools designed to help them access
                their plan, communicate, Check-In, and maintain awareness of family members when circumstances become
                uncertain. Government-sponsored deployments can also provide authorized officials with the ability to
                distribute concise emergency and public-safety communications to participating residents.
              </p>
              <p className="mrf-body">This creates two complementary levels of preparedness:</p>
              <div className="gov-levels">
                <div className="gov-level">
                  <strong>Government → Community</strong>
                  <span>Officials communicate critical emergency information.</span>
                </div>
                <div className="gov-level">
                  <strong>Family → Family</strong>
                  <span>
                    Household members use the preparedness tools and family plan they established before the emergency.
                  </span>
                </div>
              </div>
              <p className="mrf-body">
                MYREDFOLDER® is not intended to replace a government's existing emergency warning or
                emergency-management systems. It provides an additional family-centered preparedness capability that can
                complement the systems and procedures already serving the community.
              </p>
            </div>
            <div className="gov-phase__media">
              <img src={APP_SCREENSHOT} alt="MYREDFOLDER® app on a smartphone." loading="lazy" />
            </div>
          </div>
        </div>

        <div className="gov-phase">
          <div className="mrf-container gov-phase__grid">
            <div>
              <span className="gov-phase__tag">AFTER</span>
              <h3 className="mrf-heading">Help Families Begin the Road to Recovery</h3>
              <p className="mrf-callout">The emergency ending does not mean the emergency is over for the family.</p>
              <p className="mrf-body">
                Residents may return to damaged homes. Important paperwork may be inaccessible or destroyed. Families
                may need insurance information, photographs, household records, or documentation of property they owned
                before the loss.
              </p>
              <p className="mrf-body">
                MYREDFOLDER® extends preparedness into recovery by helping families organize important information
                before it is lost or inaccessible. A household's home inventory and supporting documentation may become
                particularly valuable when residents begin communicating with insurers or seeking disaster assistance.
              </p>
              <p className="mrf-body">
                At the community level, authorized officials can continue communicating relevant recovery information to
                participating residents as the community transitions from immediate response into recovery.
              </p>
            </div>
            <div className="gov-phase__media">
              <img src={FLOOD_PHOTO} alt="Flooded houses in a community after a storm." loading="lazy" />
            </div>
          </div>
        </div>
      </section>
      <section className="gov-band gov-band--red" aria-label="Recovery">
        <div className="mrf-container">
          <p className="gov-band__text">Preparedness should not end when the storm passes.</p>
        </div>
      </section>

      {/* 6. White-label showcase */}
      <section className="gov-white" aria-labelledby="gov-white-heading">
        <div className="mrf-container">
          <div className="gov-white__grid">
          <div className="gov-white__copy">
            <span className="gov-white__eyebrow">White-label deployment</span>
            <h2 id="gov-white-heading" className="mrf-heading">
              Your Community. Your Identity. Powered by MYREDFOLDER®.
            </h2>
            <div className="gov-white__brand">
              {/* <strong>[COMMUNITY NAME] CITIZEN EMERGENCY PREPAREDNESS PLAN</strong> */}
              <strong>CITIZEN EMERGENCY PREPAREDNESS PLAN</strong>
              <small>Powered by MYREDFOLDER®</small>
            </div>
            <p className="mrf-body gov-white__lead">
              MYREDFOLDER® can support a white-labeled government deployment, allowing a participating jurisdiction to
              establish a locally identified family preparedness program powered by MYREDFOLDER® technology.
            </p>
            <p className="mrf-body gov-white__lead">
              Residents could access the program through their government's website and enter an experience identified
              with their community's preparedness initiative. This allows a mayor, county executive, tribal government,
              emergency management agency, or other participating jurisdiction to establish a recognizable preparedness
              initiative for the people it serves—while MYREDFOLDER® provides the underlying family preparedness
              technology.
            </p>
          </div>

          <div className="gov-devices">
            <img
              src={SCREEN_FORMAT}
              alt="The same community-branded preparedness program shown on a desktop, tablet and smartphone."
              loading="lazy"
            />
          </div>
        </div>
        </div>
      </section>

      {/* 7. Resident experience */}
      <section className="mrf-section" aria-labelledby="gov-flow-heading">
        <div className="mrf-container">
          <h2 id="gov-flow-heading" className="mrf-heading mrf-heading--center">
            A Straightforward Resident Experience
          </h2>
          <ol className="gov-flow">
            {flowSteps.map((step, i) => (
              <li key={step.label} className="gov-flow__step">
                <div className="gov-flow__num">{i + 1}</div>
                <div className="gov-flow__label">{step.label.toUpperCase()}</div>
                <p>{step.body}</p>
              </li>
            ))}
          </ol>
          <p className="gov-tagline gov-center" style={{ textAlign: "center" }}>
            Government makes preparedness available. Families make preparedness personal.
          </p>
        </div>
      </section>

      {/* 8. More than an alert */}
      <section className="mrf-section mrf-section--alt" aria-labelledby="gov-alert-heading">
        <div className="mrf-container">
          <h2 id="gov-alert-heading" className="mrf-heading mrf-heading--center">
            More Than an Emergency Alert
          </h2>
          <p className="mrf-lead mrf-lead--center">
            Many communities already have systems capable of telling residents that an emergency is occurring.
            MYREDFOLDER® addresses a different question:
          </p>
          <p className="gov-tagline" style={{ textAlign: "center", marginTop: 0 }}>
            What happens after the resident receives the alert?
          </p>

          <div className="gov-next">
            <div className="gov-next__alert">
              <span className="gov-next__pill">An alert tells a resident</span>
              <p className="gov-next__big">Something is happening.</p>
            </div>
            <div className="gov-next__arrow" aria-hidden="true" />
            <div className="gov-next__plan">
              <span className="gov-next__pill gov-next__pill--red">A family preparedness plan helps them decide</span>
              <p className="gov-next__big">What happens next.</p>
            </div>
          </div>

          <div className="gov-qgroups">
            {afterAlertGroups.map((g) => (
              <div key={g.theme} className="gov-qgroup">
                <h3 className="gov-qgroup__theme">{g.theme}</h3>
                <ul>
                  {g.questions.map((q) => (
                    <li key={q}>{q}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <p className="gov-band__text gov-next__close">
            That is the role MYREDFOLDER® is designed to help fulfill.
          </p>
        </div>
      </section>

      {/* 9. Designed for */}
      <section className="mrf-section gov-aud" aria-labelledby="gov-audience-heading">
        <div className="mrf-container">
          <div className="gov-aud__grid">
            <div className="gov-aud__intro">
              <span className="gov-aud__eyebrow">Who it's for</span>
              <h2 id="gov-audience-heading" className="gov-aud__title">
                Designed for the Communities Government Serves
              </h2>
              <p className="gov-aud__text">
                MYREDFOLDER® can be explored for a variety of government and community environments.
              </p>
              <p className="gov-aud__goal">
                Whether serving several thousand residents or a much larger population, the objective remains the
                same: <strong>help more families prepare before they need the plan.</strong>
              </p>
            </div>
            <ul className="gov-aud__tiles">
              {audiences.map((a, i) => (
                <li key={a} className="gov-aud__tile">
                  <span className="gov-aud__num">{String(i + 1).padStart(2, "0")}</span>
                  <span className="gov-aud__name">{a}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* 10. Why this matters */}
      <section className="mrf-section mrf-section--alt" aria-labelledby="gov-leaders-heading">
        <div className="mrf-container">
          <h2 id="gov-leaders-heading" className="mrf-heading">
            Why This Matters to Government Leaders
          </h2>
          <p className="mrf-lead">
            A mayor, city manager, emergency manager, or public-safety official should leave this page understanding
            that MYREDFOLDER® is not simply another consumer application. The larger opportunity is to strengthen the
            preparedness culture of the community. A government-sponsored MYREDFOLDER® initiative can help a
            jurisdiction:
          </p>
          <div className="gov-cards">
            {leaderBenefits.map((b) => (
              <div key={b.title} className="gov-card">
                <h3>{b.title}</h3>
                <p>{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 11. Imagine */}
      <section
        className="gov-imagine"
        aria-labelledby="gov-imagine-heading"
        style={{ backgroundImage: `url(${FAMILY_PET_PHOTO})` }}
      >
        <div className="mrf-container">
          <h2 id="gov-imagine-heading">Imagine Your Community Better Prepared</h2>
          <ul>
            {imagineLines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          <p style={{ fontSize: 20, lineHeight: 1.5 }}>
            That is the opportunity MYREDFOLDER® brings to government. It is not about predicting the next emergency.{" "}
            <strong>It is about helping your community prepare for it.</strong>
          </p>
        </div>
      </section>

      {/* 12. Final CTA */}
      <section className="gov-final" aria-labelledby="gov-cta-heading">
        <div className="mrf-container">
          <div className="gov-final__grid">
            <div className="gov-final__copy">
              <span className="gov-final__eyebrow">Next step</span>
              <h2 id="gov-cta-heading" className="gov-final__title">
                Let's Build a More Prepared Community
              </h2>
              <p className="gov-final__promise">
                Your community already has emergency plans. Let's help your families have one too.
              </p>
              <p className="gov-final__text">
                Every jurisdiction is different. Population, hazards, existing technology, emergency-management
                structure, enrollment requirements, branding, implementation needs, and funding considerations can vary
                considerably.
              </p>
              <p className="gov-final__text">
                Tell us about your community, the residents you serve, and your preparedness objectives. The
                MYREDFOLDER® team can explore with you what a government-sponsored deployment could look like for your
                jurisdiction.
              </p>
            </div>

            <div className="gov-final__actions">
              <div className="gov-final__card gov-final__card--primary">
                <p className="gov-final__step">That's why the first step isn't purchasing a product.</p>
                <p className="gov-final__step-strong">The first step is a conversation.</p>
                <button type="button" className="mrf-btn mrf-btn--primary" onClick={openForm}>
                  Explore a Government Partnership
                </button>
                <p className="gov-final__note">Complete our brief Government Partnership Questionnaire.</p>
              </div>
              <div className="gov-final__card">
                <p className="gov-final__card-title">Prefer to speak with us directly?</p>
                <p className="gov-final__contact">
                  {CONTACT_PHONE_LABEL} | {CONTACT_PHONE}
                </p>
                <a className="gov-final__contact gov-final__mail" href={`mailto:${CONTACT_EMAIL}`}>
                  {CONTACT_EMAIL}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {formOpen && <GovermentModalForm onOpen onClose={closeForm} close={closeForm} />}
    </div>
  );
};

const GovernmentPage = () => <ContentWrapper contenview={<Government />} />;

export default GovernmentPage;
